import { useEffect, useState } from "react";
import { Skeleton, message } from "antd";
import BrandFont, { BrandFontType } from "../../../../brand/BrandFont";
import BrandKitApi from "../../../../../api/BrandKitApi";
import DashboardApi from "../../../../../api/DashboardApi";
import { FontDto, UploadedFontDto } from "../../../../../domain/BrandDataDto";
import { BrandKitDto, TimberFont } from "../../../../../generated/dashboard-client";
import "./FontsTab.scss";

export function FontsTab({ selected }: { selected: BrandKitDto }) {
  const [header, setHeader] = useState<FontDto>({} as FontDto);
  const [subHeader, setSubHeader] = useState<FontDto>({} as FontDto);
  const [bodyText, setBodyText] = useState<FontDto>({} as FontDto);
  const [uploadedFonts, setUploadedFonts] = useState<UploadedFontDto[]>([]);
  const [timberFonts, setTimberFonts] = useState<TimberFont[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    DashboardApi.getFonts().then((resp) => setTimberFonts(resp.data));
  }, []);

  useEffect(() => {
    if (!selected.id) return;
    setLoading(true);
    BrandKitApi.getFonts(selected.id)
      .then((fontsResp) => {
        const data = fontsResp.data || {};
        setHeader((data.fontHeader || {}) as FontDto);
        setSubHeader((data.fontSubheader || {}) as FontDto);
        setBodyText((data.fontBodyText || {}) as FontDto);

        const uploaded = ((data.uploadedFonts || []) as unknown as UploadedFontDto[]).sort(
          (a, b) => a.name.localeCompare(b.name)
        );
        setUploadedFonts(uploaded);

        try {
          uploaded.forEach((uf: UploadedFontDto) => {
            const url = `url('https://mhm-timber.s3.amazonaws.com/${uf.path.replace(/ /g, "%20")}')`;
            const font = new FontFace(uf.name, url);
            font.load();
            document.fonts.add(font);
          });
        } catch (err) {
          console.error(err);
        }
      })
      .catch(() => message.error("Failed to load fonts"))
      .finally(() => setLoading(false));
  }, [selected.id]);

  function saveBrandFonts(updated: { header?: FontDto; subHeader?: FontDto; bodyText?: FontDto }) {
    if (!selected.id) return;
    const h = updated.header ?? header;
    const s = updated.subHeader ?? subHeader;
    const b = updated.bodyText ?? bodyText;
    BrandKitApi.saveFonts(selected.id, {
      fontHeader: h?.name ? h as any : undefined,
      fontSubheader: s?.name ? s as any : undefined,
      fontBodyText: b?.name ? b as any : undefined,
    }).catch(() => message.error("Failed to save fonts"));
  }

  function onHeaderChange(font: FontDto) {
    setHeader(font);
    saveBrandFonts({ header: font });
  }

  function onSubHeaderChange(font: FontDto) {
    setSubHeader(font);
    saveBrandFonts({ subHeader: font });
  }

  function onBodyTextChange(font: FontDto) {
    setBodyText(font);
    saveBrandFonts({ bodyText: font });
  }

  return (
    <div className="fonts-tab">
      {loading ? (
        <Skeleton active />
      ) : (
        <section className="brand-kit-brand-fonts">
          <div className="brand-kit-section-label">Brand Fonts</div>
          <ul>
            <li>
              <BrandFont
                type={BrandFontType.Header}
                label="Header"
                font={header}
                fonts={timberFonts}
                uploadedFonts={uploadedFonts}
                onChange={onHeaderChange}
              />
            </li>
            <li>
              <BrandFont
                type={BrandFontType.SubHeader}
                label="Subheader"
                font={subHeader}
                fonts={timberFonts}
                uploadedFonts={uploadedFonts}
                onChange={onSubHeaderChange}
              />
            </li>
            <li>
              <BrandFont
                type={BrandFontType.BodyText}
                label="Body text"
                font={bodyText}
                fonts={timberFonts}
                uploadedFonts={uploadedFonts}
                onChange={onBodyTextChange}
              />
            </li>
          </ul>
        </section>
      )}
    </div>
  );
}
